import type { Path } from 'react-hook-form';
import { SignupOrganisationFormFields } from './signupOrganisationForm.types';

/** RHF/API roots: broker firm (`firm`), network, mortgage club — create-organisation flow. */
export const brokerOrgFormRoot = {
  firm: 'firm',
  network: 'network',
  mortgageClub: 'mortgageClub',
} as const;

export type BrokerOrgFormRootKey = (typeof brokerOrgFormRoot)[keyof typeof brokerOrgFormRoot];

/** Same roots wherever the shared address block is used (firm / network / mortgage club). */
export type BrokerOrgAddressSection = BrokerOrgFormRootKey;

export const addressRoot = 'address' as const;

/** Leaf keys for `group.address` — one shape for firm, network, mortgage club. */
export const addressField = {
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  postcode: 'postcode',
  town: 'town',
  county: 'county',
  country: 'country',
} as const;

/** Shared one-level fields under any org section. */
export const orgField = {
  backendId: 'backendId',
  name: 'name',
  financialConductAuthorityNumber: 'financialConductAuthorityNumber',
  isAddressManual: 'isAddressManual',
} as const;

export const firmField = {
  companyNumber: 'companyNumber',
  principalOrDirectorName: 'principalOrDirectorName',
  phoneNumber: 'phoneNumber',
  website: 'website',
  informationCommissionersOfficeNumber: 'informationCommissionersOfficeNumber',
  authorisationType: 'authorisationType',
  hasHppPermissions: 'hasHppPermissions',
  isPartOfClub: 'isPartOfClub',
} as const;

export const networkField = {
  isPartOfMortgageClub: 'isPartOfMortgageClub',
  isStrideUpOnPanel: 'isStrideUpOnPanel',
} as const;

/**
 * Dotted RHF field path: `group` + one or more segments, e.g.
 * `formPath(brokerOrgFormRoot.mortgageClub, orgField.backendId)` → `mortgageClub.backendId`
 * `formPath(brokerOrgFormRoot.firm, addressRoot, addressField.town)` → `firm.address.town`
 */
export function formPath(
  group: BrokerOrgFormRootKey,
  ...segments: [string, ...string[]]
): Path<SignupOrganisationFormFields> {
  return [group, ...segments].join('.') as Path<SignupOrganisationFormFields>;
}

export function orgAddressPath(
  group: BrokerOrgFormRootKey,
  key: (typeof addressField)[keyof typeof addressField],
): Path<SignupOrganisationFormFields> {
  return formPath(group, addressRoot, key);
}

export function isAddressManualPathFor(group: BrokerOrgFormRootKey) {
  return formPath(group, orgField.isAddressManual);
}

export function orgAddressValuePath(group: BrokerOrgFormRootKey) {
  return formPath(group, addressRoot);
}
